import React, {
    useState,
    useContext,
    useRef,
    useEffect,
    useCallback,
} from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { UserSettingsContext } from "../context/userSettings";
import { useMessages } from "../context/messageContext";
import { useTooltip } from "../context/tooltipContext";
import {
    CaptionController,
    preloadCaptionOcrEngine,
    subscribeCaptionOcrPreparation,
    isTouchDevice,
} from "../utils/CaptionController";
import { websocketService } from "../services/websocketService";
import { startSelection } from "../utils/handleHighlight";
import wordLookUp from "../utils/handleWordLookUp";
import apiGetWordGlossary from "../api/apiGetWordGlossary";
import { media } from "../styles/stylesTheme";
import { getColor, borderRadius } from "../styles/stylesUtils";
import { trackCameraTranslation, trackEvent } from "../utils/tracking";
import { getCSSImageUrl } from "../utils/mediaUrl";
import {
    FREE_MONTHLY_MINUTES,
    PAID_MONTHLY_HOURS,
    PAID_PRICE_LABEL,
    TOPUP_PACK_HOURS,
    getLimitMessaging,
    getUsageMeter,
} from "../utils/billingDisplay";
import { safeSetMessages } from "../utils/MessageController";
import { getOcrProgressTarget } from "../utils/ocrProgress";
import whatToDo from "../utils/handleWhatToDo";
import ChatBox from "./ChatBox";
import NavSettings from "./NavSettings";
import Tooltip from "./Tooltip";
import ModalWordGlossary from "./ModalWordGlossary";
import TranslateToggle from "./TranslateToggle";
import LandingPage from "./LandingPage";
import apiDevLogin, { isLanDevAutoLogin } from "../api/apiDevLogin";
import ModalAddWordToFolder from "./ModalAddWordToFolder";
import Notification from "./Notification";
import WhatToDoOptions from "./WhatToDo";
import useNotifications from "../hooks/useNotifications";
import initiateGoogleAuth from "../api/apiGoogleAuth";
import apiTranslateCaption from "../api/apiTranslateCaption";
import apiBillingEntitlement from "../api/apiBillingEntitlement";
import apiCreateBillingCheckout from "../api/apiCreateBillingCheckout";
import apiCreateBillingTopupCheckout from "../api/apiCreateBillingTopupCheckout";
import apiCreateBillingPortalSession from "../api/apiCreateBillingPortalSession";
import {
    markCaptionAsProcessed,
    trimProcessedCaptionIds,
} from "../utils/captionDedupe";
import { presentCaption } from "../utils/presentCaption";
import { appendToCaptionDisplayQueue } from "../utils/captionDisplayQueue";

const Container = styled.div`
    height: 100svh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const MainContent = styled.main`
    flex: 1;
    position: relative;
    overflow: hidden;
`;

const FixedToggleWrapper = styled.div`
    position: fixed;
    top: 2em;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    transition: filter 0.3s ease, opacity 0.3s ease;
    filter: ${({ $shouldBlur }) => ($shouldBlur ? "blur(0.2em)" : "none")};
    opacity: ${({ $shouldBlur }) => ($shouldBlur ? 0.3 : 1)};

    ${media.tablet} {
        top: 2em;
        right: 2em;
        left: auto;
        transform: none;
    }
`;

const ChatWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
`;

// Video modal wrapper (matches main site ListenController)
const VideoModalWrapper = styled(motion.div)`
    position: fixed;
    bottom: 2em;
    left: 50%;
    width: 59.78em;
    max-width: 90%;
    height: 23.6em;
    min-height: 23.6em;
    z-index: 100001;

    ${media.mobile} {
        width: 100%;
        max-width: none;
        height: 30em;
        min-height: auto;
        bottom: 0svh;
    }
`;

// Player container inside wrapper
const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 23.6em;
    border-radius: 1em;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    background: black;

    ${media.mobile} {
        min-height: auto;
        aspect-ratio: 16/9;
        border-radius: 0;
    }

    video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        transform-origin: center center;
        transition: transform 0.2s ease, opacity 0.5s ease;
        opacity: ${(props) => (props.$ready ? 1 : 0)};
    }
`;

// Spinner overlay for loading state
const SpinnerOverlay = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1em;
    z-index: 1002;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);

    ${media.mobile} {
        border-radius: 0;
    }
`;

const Spinner = styled.div`
    width: 3em;
    height: 3em;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const OcrProgress = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8em;
    width: min(60%, 20em);
`;

const OcrProgressLabel = styled.div`
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9em;
    text-align: center;
`;

const OcrProgressTrack = styled.div`
    width: 100%;
    height: 0.4em;
    border-radius: 0.2em;
    background: rgba(255, 255, 255, 0.2);
    overflow: hidden;
`;

const OcrProgressFill = styled.div`
    height: 100%;
    border-radius: 0.2em;
    background: white;
    transition: width 0.2s linear;
`;

// Close button matching main site
const CloseButton = styled.button`
    position: absolute;
    top: -0.8em;
    right: -0.8em;
    width: 2.8em;
    height: 2.8em;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    z-index: 1003;
    transition: all 0.2s ease;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);

    &:hover {
        background: ${getColor("colorAccent", 0.8)};
        transform: scale(1.1);
    }

    ${media.mobile} {
        right: 3em;
        top: -1.3em;
        background: ${getColor("colorAccent")};
        transform: scale(1.3);
    }
`;

const CloseIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    width: 0.8em;
    height: 0.8em;
    background: white;
    mask: url("${getCSSImageUrl("img/icon-x.svg")}") center center / 100%
        no-repeat;
    -webkit-mask: url("${getCSSImageUrl("img/icon-x.svg")}") center center /
        100% no-repeat;
`;

// Zoom controls at bottom
const VideoControls = styled.div`
    position: absolute;
    bottom: 0.5em;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.25em;
    align-items: center;
    z-index: 10;
    font-size: 2em;
`;

const ZoomButton = styled.button`
    width: 1.75em;
    height: 1.75em;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 1em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
`;

const ZoomLabel = styled.span`
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 0.6875em;
    padding: 0.25em 0.5em;
    border-radius: 0.75em;
    min-width: 2.5em;
    text-align: center;
`;

const SwitchCameraButton = styled(ZoomButton)`
    margin-left: 0.25em;

    &:disabled {
        cursor: wait;
        opacity: 0.55;
    }
`;

const LimitExceededOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${getColor("gradL", 0.95)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    padding: 2em;
    text-align: center;

    h2 {
        font-size: 1.8em;
        margin-bottom: 0.5em;
        color: ${getColor("black", 1)};
    }

    p {
        font-size: 1.1em;
        color: ${getColor("black", 0.7)};
        max-width: 400px;
        line-height: 1.5;
        margin-bottom: 0.75em;
    }
`;

const LimitActions = styled.div`
    margin-top: 1em;
    display: flex;
    gap: 0.75em;
    flex-wrap: wrap;
    justify-content: center;
`;

const LimitActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: ${getColor("colorCorrect", 1)};
    color: ${getColor("white", 1)};
    font-family: var(--bold);
    font-size: 1em;
    padding: 0.7em 1em;
    ${borderRadius(0.5)}
    cursor: pointer;
    min-width: 11em;
    transition: filter 0.2s ease;

    &:hover {
        filter: brightness(1.08);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.65;
    }
`;

const LimitMeta = styled.p`
    font-size: 0.95em !important;
    color: ${getColor("black", 0.55)} !important;
    margin-top: 0.25em;
    margin-bottom: 0 !important;
    max-width: 420px;
    line-height: 1.45;
`;

// Always-visible usage meter (Fix 2)
const UsageMeter = styled.div`
    position: fixed;
    bottom: 1.2em;
    left: 1.2em;
    z-index: 60000;
    display: flex;
    flex-direction: column;
    gap: 0.4em;
    padding: 0.6em 0.85em;
    background: ${getColor("colorUI", 0.6)};
    backdrop-filter: blur(1em);
    -webkit-backdrop-filter: blur(1em);
    ${borderRadius(0.6)}
    box-shadow: 0 0.1em 0.4em 0 ${getColor("black", 0.08)};
    pointer-events: none;
    user-select: none;
    max-width: 15em;

    ${media.mobile} {
        bottom: 1em;
        left: 1em;
        font-size: 0.92em;
    }
`;

const UsageMeterLabel = styled.span`
    font-size: 0.72em;
    color: ${getColor("black", 0.62)};
    font-family: var(--bold);
    white-space: nowrap;
    letter-spacing: 0.01em;
`;

const UsageMeterTrack = styled.div`
    width: 100%;
    height: 0.3em;
    border-radius: 0.3em;
    overflow: hidden;
    background: ${getColor("black", 0.1)};
`;

const UsageMeterFill = styled.div`
    height: 100%;
    width: ${({ $fraction }) =>
        `${Math.max(0, Math.min(1, $fraction || 0)) * 100}%`};
    background: ${(props) =>
        props.$low
            ? getColor("colorAccent", 0.9)(props)
            : getColor("colorCorrect", 0.85)(props)};
    border-radius: 0.3em;
    transition: width 0.4s ease, background 0.3s ease;
`;

// Checkout-return overlay pieces (Fix 1)
const CheckoutSpinner = styled.div`
    width: 2.6em;
    height: 2.6em;
    border: 3px solid ${getColor("black", 0.15)};
    border-top: 3px solid ${getColor("colorCorrect", 1)};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1em;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const CheckoutCheck = styled.div`
    width: 3em;
    height: 3em;
    border-radius: 50%;
    background: ${getColor("colorCorrect", 1)};
    color: ${getColor("white", 1)};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6em;
    line-height: 1;
    margin-bottom: 0.5em;
`;

const LimitActionButtonSecondary = styled(LimitActionButton)`
    background: transparent;
    color: ${getColor("black", 0.7)};
    box-shadow: inset 0 0 0 0.1em ${getColor("black", 0.15)};

    &:hover {
        filter: none;
        background: ${getColor("black", 0.05)};
    }
`;

const formatResetDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const getSubscriptionButtonDetails = (entitlement) => {
    const parsedHours = Number(entitlement?.monthly_paid_hours_total);
    const hours =
        Number.isFinite(parsedHours) && parsedHours > 0
            ? parsedHours
            : PAID_MONTHLY_HOURS;
    const price = entitlement?.pricing?.paid_label || PAID_PRICE_LABEL;
    return `${hours} hours, ${price}`;
};

const resolveClientCountryCode = () => {
    const getRegionFromLocale = (locale) => {
        if (!locale || typeof locale !== "string") return null;
        const match = locale.match(/[-_]([A-Za-z]{2})(?:[-_]|$)/);
        if (!match) return null;
        return match[1].toUpperCase();
    };

    if (typeof window === "undefined") return null;

    const localeCandidates = [
        Intl.DateTimeFormat().resolvedOptions().locale,
        ...(Array.isArray(navigator?.languages) ? navigator.languages : []),
        navigator?.language,
    ];

    for (const locale of localeCandidates) {
        const countryCode = getRegionFromLocale(locale);
        if (countryCode) return countryCode;
    }

    return null;
};

const PromoModalBackdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
`;

const PromoModalContent = styled(motion.div)`
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 100000;
    font-size: 1em;
    width: 32em;
    max-width: 95vw;
    border-radius: 1.25em;
    overflow: hidden;
    color: ${getColor("black", 1)};
    background: ${getColor("white", 0.7)};
    box-shadow: 0 20px 60px -12px ${getColor("black", 0.25)},
        0 8px 24px -8px ${getColor("black", 0.1)};
    pointer-events: auto;
    text-align: center;
    padding: 1em 2em 2.4em;
    backdrop-filter: blur(1em);
    box-sizing: border-box;

    ${media.mobile} {
        width: 92%;
        max-width: 92%;
        font-size: 1em;
        border-radius: 1em;
    }

    h2 {
        font-size: 1.4em;
        margin-bottom: 0.6em;
        font-family: var(--bold);
    }

    p {
        font-size: 1.05em;
        color: ${getColor("black", 0.7)};
        line-height: 1.5;
        margin-bottom: 1.5em;
    }

    a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: var(--bold);
        font-size: 1.2em;
        background: linear-gradient(
            180deg,
            ${getColor("colorCorrect", 1)} 0%,
            ${getColor("colorCorrect", 0.85)} 100%
        );
        color: white;
        text-shadow: 0 1px 0 ${getColor("colorCorrect", 0.6)};
        text-decoration: none;
        border: none;
        ${borderRadius(0.5)}
        cursor: pointer;
        transition: 0.15s ease;
        position: relative;
        user-select: none;
        -webkit-user-select: none;
        box-shadow: 0 0.1em 0 0 #eb450e;

        span {
            font-size: 1.2em;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.7em 1.1em 0.8em;
            ${borderRadius(0.48)}
            box-shadow: inset 0 0.1em 0.1em 0 ${getColor("white", 0.3)},
                inset 0 0 0.1em 0 ${getColor("black", 0.5)};
        }

        &:hover {
            filter: brightness(1.1);
        }

        &:active {
            transform: translateY(0.08em) scale(0.997);
            box-shadow: 0 0 0 0 #eb450e;
        }
    }
`;

const Main = () => {
    const { isLoggedIn, translateMode, bjornUser, settings } =
        useContext(UserSettingsContext);
    const demoMode =
        process.env.NODE_ENV === "development"
            ? new URLSearchParams(window.location.search).get("demo")
            : null;
    const isFreeLimitDemo =
        demoMode === "free-limit" || demoMode === "free-limit-active";
    const isActiveFreeLimitDemo = demoMode === "free-limit-active";
    const { messages, setMessages, addMessage, clearMessages } = useMessages();
    const tooltip = useTooltip();

    // Add word to folder modal state
    const [showAddWordModal, setShowAddWordModal] = useState(false);
    const [addWordTooltipData, setAddWordTooltipData] = useState({
        word: "",
        definition: "",
    });
    const [showBjornPromoModal, setShowBjornPromoModal] = useState(false);

    // Notifications
    const { currentNotification, triggerNotification } = useNotifications();

    const [isStreaming, setIsStreaming] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
    const [limitExceeded, setLimitExceeded] = useState(false);
    const [limitErrorData, setLimitErrorData] = useState(null);
    const [billingEntitlement, setBillingEntitlement] = useState(null);
    const [isBillingActionLoading, setIsBillingActionLoading] = useState(false);
    const [isPortalActionLoading, setIsPortalActionLoading] = useState(false);
    // Daily IP-cap limit message (Fix 3) — shown without a subscribe button
    const [dailyCapMessage, setDailyCapMessage] = useState(null);
    // Checkout return handling (Fix 1): "paid" | "topup" | null
    const [checkoutReturn, setCheckoutReturn] = useState(null);
    // Checkout status: "activating" | "success" | "processing" | null
    const [checkoutStatus, setCheckoutStatus] = useState(null);

    // Delay video fade-in by 150ms after camera is ready
    useEffect(() => {
        if (cameraReady) {
            const timer = setTimeout(() => setShowVideo(true), 150);
            return () => clearTimeout(timer);
        } else {
            setShowVideo(false);
        }
    }, [cameraReady]);

    // Word glossary state
    const [showWordGlossary, setShowWordGlossary] = useState(false);
    const [glossaryData, setGlossaryData] = useState(null);
    const [isLoadingGlossary, setIsLoadingGlossary] = useState(false);

    // Nav and modal states for blur effect
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

    // Check if mobile/tablet
    const checkIfMobile = () => window.innerWidth <= 1024;

    const cameraContainerRef = useRef(null);
    const captionControllerRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const tooltipRef = useRef(null);
    const chatWrapperRef = useRef(null);
    const hasReceivedCaptionRef = useRef(false);
    const processedCaptionIdsRef = useRef(new Set());
    const translateModeRef = useRef(translateMode);
    const captionPresentationQueueRef = useRef(Promise.resolve());
    const captionDisplayPausedRef = useRef(false);
    const queuedCaptionMessagesRef = useRef([]);
    const hasShownTranslateInstructionRef = useRef(false);
    const isBillingActionLoadingRef = useRef(false);
    const hasShownFreeLimitMessageRef = useRef(false);
    const translationNotificationAtRef = useRef(0);

    const notifyTranslationUnavailable = useCallback(() => {
        const now = Date.now();
        if (now - translationNotificationAtRef.current < 60000) return;
        translationNotificationAtRef.current = now;
        triggerNotification(
            'Translation is unavailable. Showing the original captions for now.',
            'warn'
        );
    }, [triggerNotification]);

    const queueCaptionForLater = useCallback((caption) => {
        queuedCaptionMessagesRef.current = appendToCaptionDisplayQueue(
            queuedCaptionMessagesRef.current,
            caption
        );
    }, []);

    const scheduleCaptionPresentation = useCallback(
        (caption) => {
            const { message, autoTranslate } = caption;
            const display = async () => {
                // Interaction may have started while this caption was waiting
                // behind an earlier translation, so check again at display.
                if (captionDisplayPausedRef.current) {
                    queueCaptionForLater(caption);
                    return null;
                }

                if (
                    !autoTranslate &&
                    !hasShownTranslateInstructionRef.current
                ) {
                    hasShownTranslateInstructionRef.current = true;
                    addMessage({
                        id: `translate-instruction-${Date.now()}`,
                        content: "Tap or highlight to translate",
                        sender: "ai",
                    });
                }

                return presentCaption({
                    message,
                    autoTranslate,
                    translateCaption: apiTranslateCaption,
                    onTranslationUnavailable:
                        notifyTranslationUnavailable,
                    onTranslationSuccess: trackCameraTranslation,
                    addMessage,
                });
            };
            const task = captionPresentationQueueRef.current.then(
                display,
                display
            );
            captionPresentationQueueRef.current = task.catch(() => {});
            return task;
        },
        [
            addMessage,
            notifyTranslationUnavailable,
            queueCaptionForLater,
        ]
    );

    const addCaptionForCurrentMode = useCallback(
        (message) => {
            const caption = {
                message,
                autoTranslate: translateModeRef.current,
            };

            if (captionDisplayPausedRef.current) {
                queueCaptionForLater(caption);
                return Promise.resolve(null);
            }

            return scheduleCaptionPresentation(caption);
        },
        [queueCaptionForLater, scheduleCaptionPresentation]
    );

    useEffect(() => {
        translateModeRef.current = translateMode;
    }, [translateMode]);

    const shouldPauseCaptionDisplay =
        tooltip.isHighlighting ||
        showWordGlossary ||
        showAddWordModal ||
        showBjornPromoModal ||
        isFeedbackModalOpen;

    useEffect(() => {
        captionDisplayPausedRef.current = shouldPauseCaptionDisplay;
        if (
            shouldPauseCaptionDisplay ||
            queuedCaptionMessagesRef.current.length === 0
        ) {
            return;
        }

        const queuedCaptions = queuedCaptionMessagesRef.current;
        queuedCaptionMessagesRef.current = [];
        queuedCaptions.forEach(scheduleCaptionPresentation);
    }, [shouldPauseCaptionDisplay, scheduleCaptionPresentation]);

    useEffect(() => {
        if (isLoggedIn) {
            preloadCaptionOcrEngine();
        }
    }, [isLoggedIn]);

    // Desktop OCR downloads ~31MB of models on a cold cache; show real
    // progress instead of a spinner. Touch devices read captions server-side
    // and never load an engine.
    const [ocrPreparation, setOcrPreparation] = useState(null);
    useEffect(() => {
        if (isTouchDevice()) return undefined;
        return subscribeCaptionOcrPreparation(setOcrPreparation);
    }, []);

    // Ticks the displayed percentage toward its target so the bar keeps
    // moving smoothly through engine startup, which has no byte progress.
    const [ocrProgressPercent, setOcrProgressPercent] = useState(0);
    useEffect(() => {
        if (!ocrPreparation) return undefined;
        if (!["downloading", "starting"].includes(ocrPreparation.phase)) {
            setOcrProgressPercent(
                ocrPreparation.phase === "ready" ? 100 : 0
            );
            return undefined;
        }

        const tick = () =>
            setOcrProgressPercent((current) =>
                getOcrProgressTarget(ocrPreparation, current)
            );
        tick();
        const interval = setInterval(tick, 150);
        return () => clearInterval(interval);
    }, [ocrPreparation]);

    const refreshBillingEntitlement = useCallback(async () => {
        try {
            const entitlement = await apiBillingEntitlement();
            if (entitlement) {
                setBillingEntitlement(entitlement);
                if (entitlement.can_stream) {
                    setLimitExceeded(false);
                    setLimitErrorData(null);
                    hasShownFreeLimitMessageRef.current = false;
                }
            }
            return entitlement;
        } catch (error) {
            console.error("Failed to load billing entitlement:", error);
            return null;
        }
    }, []);

    useEffect(() => {
        if (isFreeLimitDemo) return;

        if (!isLoggedIn) {
            setBillingEntitlement(null);
            setLimitExceeded(false);
            setLimitErrorData(null);
            hasShownFreeLimitMessageRef.current = false;
            return;
        }

        refreshBillingEntitlement();
    }, [isLoggedIn, isFreeLimitDemo, refreshBillingEntitlement]);

    const dismissCheckoutReturn = useCallback(() => {
        setCheckoutReturn(null);
        setCheckoutStatus(null);
    }, []);

    // Fix 1: detect the Polar checkout success redirect on boot, clean the URL
    // immediately, and enter the "activating" state.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        if (status === "captions_paid" || status === "captions_topup") {
            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            );
            setCheckoutReturn(status === "captions_paid" ? "paid" : "topup");
            setCheckoutStatus("activating");
        }
    }, []);

    // Fix 1: poll entitlement until the purchase is reflected (max ~20s).
    useEffect(() => {
        if (!checkoutReturn || !isLoggedIn) return undefined;
        if (checkoutStatus === "success" || checkoutStatus === "processing") {
            return undefined;
        }

        let cancelled = false;
        let attempts = 0;
        let baselineTopup = null;
        const maxAttempts = 10; // ~20s at 2s intervals

        const isConfirmed = (entitlement) => {
            if (!entitlement) return false;
            if (checkoutReturn === "paid") {
                return (
                    entitlement.plan_code === "captions_paid" &&
                    entitlement.can_stream === true
                );
            }
            // topup: balance increased vs first sample, or streaming re-enabled
            const balance = entitlement.topup_seconds_balance ?? 0;
            if (baselineTopup === null) {
                baselineTopup = balance;
                return entitlement.can_stream === true;
            }
            return entitlement.can_stream === true || balance > baselineTopup;
        };

        const poll = async () => {
            if (cancelled) return;
            const entitlement = await refreshBillingEntitlement();
            if (cancelled) return;

            if (isConfirmed(entitlement)) {
                setCheckoutStatus("success");
                return;
            }

            attempts += 1;
            if (attempts >= maxAttempts) {
                setCheckoutStatus("processing");
                return;
            }
            setTimeout(poll, 2000);
        };

        poll();

        return () => {
            cancelled = true;
        };
    }, [checkoutReturn, checkoutStatus, isLoggedIn, refreshBillingEntitlement]);

    // Fix 1: auto-dismiss the success confirmation back to the normal app.
    useEffect(() => {
        if (checkoutStatus !== "success") return undefined;
        const timer = setTimeout(() => {
            dismissCheckoutReturn();
        }, 3500);
        return () => clearTimeout(timer);
    }, [checkoutStatus, dismissCheckoutReturn]);

    const handleBillingAction = useCallback(
        async (type) => {
            if (!settings?.email || isBillingActionLoadingRef.current) return;
            isBillingActionLoadingRef.current = true;
            setIsBillingActionLoading(true);

            try {
                const countryCode = resolveClientCountryCode();
                const payload = {
                    email: settings.email,
                    ...(countryCode ? { countryCode } : {}),
                };
                const result =
                    type === "topup"
                        ? await apiCreateBillingTopupCheckout(payload)
                        : await apiCreateBillingCheckout(payload);

                if (result?.url) {
                    window.location.href = result.url;
                    return;
                }
                throw new Error("Missing checkout URL");
            } catch (error) {
                console.error("Failed to create billing checkout:", error);
                triggerNotification(
                    "Could not open checkout. Please try again.",
                    "error"
                );
            } finally {
                isBillingActionLoadingRef.current = false;
                setIsBillingActionLoading(false);
            }
        },
        [settings?.email, triggerNotification]
    );

    const handleManageSubscription = useCallback(async () => {
        if (isPortalActionLoading) return;
        setIsPortalActionLoading(true);

        try {
            const result = await apiCreateBillingPortalSession();
            if (result?.url) {
                window.location.href = result.url;
                return;
            }
            throw new Error("Missing customer portal URL");
        } catch (error) {
            console.error("Failed to open customer portal:", error);
            triggerNotification(
                "Could not open subscription portal. Please try again.",
                "error"
            );
        } finally {
            setIsPortalActionLoading(false);
        }
    }, [isPortalActionLoading, triggerNotification]);

    const closeStreamUi = useCallback(() => {
        queuedCaptionMessagesRef.current = [];
        if (captionControllerRef.current) {
            captionControllerRef.current.cleanup();
            captionControllerRef.current = null;
        }
        setIsStreaming(false);
        setCameraReady(false);
        setShowVideo(false);
        setHasMultipleCameras(false);
        setIsSwitchingCamera(false);
    }, []);

    const handleLimitExceeded = useCallback(
        (payload = null) => {
            // Daily IP cap (Fix 3): show the server's "try again tomorrow"
            // message with NO subscribe/top-up button — buying wouldn't help.
            if (payload?.reason === "daily_ip_cap") {
                closeStreamUi();
                setDailyCapMessage(
                    payload?.message ||
                        payload?.error ||
                        "You've reached today's usage limit. Please try again tomorrow."
                );
                return;
            }

            const planCode =
                payload?.planCode ||
                payload?.plan_code ||
                billingEntitlement?.plan_code ||
                "free";

            closeStreamUi();
            setLimitErrorData(payload || null);
            refreshBillingEntitlement();

            if (planCode === "captions_paid") {
                setLimitExceeded(true);
                return;
            }

            setLimitExceeded(false);

            if (hasShownFreeLimitMessageRef.current) {
                return;
            }
            hasShownFreeLimitMessageRef.current = true;
            const subscriptionDetails = getSubscriptionButtonDetails(
                payload?.entitlement || billingEntitlement
            );

            safeSetMessages(setMessages, (prevMessages) => {
                const withoutExistingLimitMessages = prevMessages.filter(
                    (message) =>
                        message.id !== "free-limit-notice" &&
                        message.id !== "free-limit-subscribe"
                );

                return [
                    ...withoutExistingLimitMessages,
                    {
                        id: "free-limit-notice",
                        sender: "aiHomeLang",
                        content:
                            "You have reached the free limit, subscribe to continue",
                    },
                    {
                        id: "free-limit-subscribe",
                        sender: "optionsGrid",
                        content: (
                            <WhatToDoOptions
                                buttonLabel="Subscribe"
                                buttonDetails={subscriptionDetails}
                                onAction={() => handleBillingAction("upgrade")}
                            />
                        ),
                    },
                ];
            });
        },
        [
            billingEntitlement,
            closeStreamUi,
            refreshBillingEntitlement,
            setMessages,
            handleBillingAction,
        ]
    );

    useEffect(() => {
        if (!isFreeLimitDemo) return undefined;

        hasInitializedRef.current = true;
        setBillingEntitlement({
            plan_code: "free",
            can_stream: false,
            monthly_free_seconds_total: FREE_MONTHLY_MINUTES * 60,
            monthly_free_seconds_used: FREE_MONTHLY_MINUTES * 60,
            monthly_free_seconds_remaining: 0,
            monthly_free_minutes_total: FREE_MONTHLY_MINUTES,
            monthly_free_minutes_used: FREE_MONTHLY_MINUTES,
            monthly_free_minutes_remaining: 0,
        });
        const existingCaptions = isActiveFreeLimitDemo
            ? [
                  {
                      id: "demo-caption-1",
                      sender: "caption",
                      content: "Vi trodde inte att vi hade någon koppling.",
                      isCaption: true,
                      marginTop: "7em",
                  },
                  {
                      id: "demo-caption-2",
                      sender: "caption",
                      content:
                          "Efter att vi hade köpt det började vi hitta bilder.",
                      isCaption: true,
                  },
                  {
                      id: "demo-caption-3",
                      sender: "caption",
                      content: "Det är ju helt underbart.",
                      isCaption: true,
                  },
              ]
            : [];
        safeSetMessages(setMessages, () => [
            ...existingCaptions,
            {
                id: "free-limit-notice",
                sender: "aiHomeLang",
                content:
                    "You have reached the free limit, subscribe to continue",
                marginTop: isActiveFreeLimitDemo ? undefined : "7em",
            },
            {
                id: "free-limit-subscribe",
                sender: "optionsGrid",
                content: (
                    <WhatToDoOptions
                        buttonLabel="Subscribe"
                        buttonDetails={getSubscriptionButtonDetails()}
                        onAction={() =>
                            triggerNotification(
                                "Demo only — a signed-in free account opens checkout here.",
                                "info",
                                true
                            )
                        }
                    />
                ),
            },
        ]);

        return () => {
            hasInitializedRef.current = false;
            hasShownFreeLimitMessageRef.current = false;
        };
    }, [
        isFreeLimitDemo,
        isActiveFreeLimitDemo,
        setMessages,
        triggerNotification,
    ]);

    // Handle opening word glossary
    const handleOpenWordGlossary = useCallback(
        async ({ word, definition, sourceLanguage }) => {
            if (!sourceLanguage) {
                triggerNotification(
                    "The caption language could not be detected. Try highlighting a longer phrase.",
                    "warn"
                );
                return;
            }

            // Hide tooltip when opening glossary
            tooltip.setShowTooltip(false);
            tooltip.setForceHideTooltip(true);
            tooltip.setIsHighlighting(false);

            // Remove highlights from text
            if (chatWrapperRef.current) {
                const highlights =
                    chatWrapperRef.current.querySelectorAll("span.highlighted");
                highlights.forEach((span) =>
                    span.classList.remove("highlighted")
                );
            }

            // Show loading state
            setIsLoadingGlossary(true);
            setShowWordGlossary(true);
            setGlossaryData(null);

            try {
                // For captions, source is detected language, target is English
                const response = await apiGetWordGlossary(
                    word,
                    sourceLanguage,
                    "en", // target language (English)
                    definition
                );

                if (response && response.glossary) {
                    setGlossaryData(response.glossary);
                }
            } catch (error) {
                console.error("Error fetching word glossary:", error);
                setShowWordGlossary(false);
            } finally {
                setIsLoadingGlossary(false);
            }
        },
        [tooltip, triggerNotification]
    );

    // Handle closing word glossary
    const handleCloseWordGlossary = useCallback(() => {
        setShowWordGlossary(false);
        tooltip.setForceHideTooltip(false);
    }, [tooltip]);

    // Handle opening add word modal (or promo modal if not bjorn user)
    const handleOpenAddWordModal = useCallback(
        ({ word, definition, sourceLanguage }) => {
            if (!sourceLanguage) {
                triggerNotification(
                    "The caption language could not be detected. Try highlighting a longer phrase.",
                    "warn"
                );
                return;
            }

            // Hide tooltip when opening modal
            tooltip.setShowTooltip(false);
            tooltip.setForceHideTooltip(true);
            tooltip.setIsHighlighting(false);

            // Remove highlights from text
            if (chatWrapperRef.current) {
                const highlights =
                    chatWrapperRef.current.querySelectorAll("span.highlighted");
                highlights.forEach((span) =>
                    span.classList.remove("highlighted")
                );
            }

            // If user doesn't have a bjorn account, show promo modal
            if (!bjornUser) {
                setShowBjornPromoModal(true);
                return;
            }

            // Set the word data and show modal
            setAddWordTooltipData({
                word: word || "",
                definition: definition || "",
                sourceLanguage,
            });
            setShowAddWordModal(true);
        },
        [tooltip, bjornUser, triggerNotification]
    );

    // Handle closing add word modal
    const handleCloseAddWordModal = useCallback(() => {
        setShowAddWordModal(false);
        tooltip.setForceHideTooltip(false);
    }, [tooltip]);

    // Handle closing promo modal
    const handleCloseBjornPromoModal = useCallback(() => {
        setShowBjornPromoModal(false);
        tooltip.setForceHideTooltip(false);
    }, [tooltip]);

    // Handle word lookup when user clicks/selects text
    const handleWordLookUp = (
        clickedWord,
        topPos,
        midXPos,
        containerElement,
        lookupId
    ) => {
        wordLookUp({
            clickedWord,
            topPos,
            midXPos,
            containerElement,
            tooltip,
            setShowWordLookupLoader: tooltip.setShowWordLookupLoader,
            lookupId,
            onOpenGlossary: handleOpenWordGlossary,
            onLimitExceeded: (payload) => {
                handleLimitExceeded(payload);
            },
            onAddWord: handleOpenAddWordModal,
            onTranslationSuccess: trackCameraTranslation,
            showAddButton: true, // Always show add button
        });
    };

    // Setup highlighting for word selection
    useEffect(() => {
        if (!isLoggedIn) return;

        const cleanup = startSelection(
            handleWordLookUp,
            tooltipRef,
            tooltip.isTooltipShowing,
            tooltip.setForceHideTooltip,
            tooltip.setShowTooltip,
            tooltip.setShowWordLookupLoader,
            tooltip.setTooltipContent,
            chatWrapperRef,
            tooltip.setIsHighlighting
        );

        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Initialize whatToDo when logged in
    useEffect(() => {
        if (isLoggedIn && !isFreeLimitDemo && !hasInitializedRef.current) {
            hasInitializedRef.current = true;
            clearMessages();
            whatToDo({
                setMessages,
                onStartStream: handleStartStream,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isFreeLimitDemo]);

    // Reset initialization when user logs out
    useEffect(() => {
        if (!isLoggedIn) {
            hasInitializedRef.current = false;
            hasReceivedCaptionRef.current = false;
            processedCaptionIdsRef.current.clear();
            hasShownTranslateInstructionRef.current = false;
            queuedCaptionMessagesRef.current = [];
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn) {
            const token = localStorage.getItem("captions_token");
            if (token) {
                websocketService.connect(token);
            }
        }
        return () => {
            websocketService.disconnect();
        };
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return undefined;

        const unsubscribe = websocketService.addListener(async (data) => {
            if (data.type === "caption" && data.text) {
                const { shouldProcess, key } = markCaptionAsProcessed(
                    processedCaptionIdsRef.current,
                    data
                );

                if (!shouldProcess) return;

                trimProcessedCaptionIds(processedCaptionIdsRef.current);

                // Clear all messages on first received caption (secondary device)
                if (!hasReceivedCaptionRef.current) {
                    hasReceivedCaptionRef.current = true;
                    hasShownTranslateInstructionRef.current = false;
                    queuedCaptionMessagesRef.current = [];
                    clearMessages();
                    await new Promise((resolve) => setTimeout(resolve, 50));

                    // Add "Receiving captions..." message before first caption
                    addMessage({
                        id: `receiving-captions-${Date.now()}`,
                        content: "Receiving captions...",
                        sender: "userHomeLang",
                        marginTop: "7em",
                    });
                    await new Promise((resolve) => setTimeout(resolve, 100));
                }

                await addCaptionForCurrentMode({
                    id: data.id || key || `caption-${Date.now()}`,
                    content: data.text,
                    sender: "caption",
                    timestamp: data.timestamp,
                    sourceLanguage: data.sourceLanguage || null,
                    analyticsSessionId: data.sessionId || null,
                });
            }
        });
        return unsubscribe;
    }, [
        isLoggedIn,
        addMessage,
        addCaptionForCurrentMode,
        clearMessages,
    ]);

    const handleStartStream = async () => {
        const entitlement = await refreshBillingEntitlement();
        if (entitlement && !entitlement.can_stream) {
            handleLimitExceeded({
                limitExceeded: true,
                planCode: entitlement.plan_code,
                nextResetAt: entitlement.next_reset_at,
            });
            return;
        }

        // First set streaming to true to show the video container
        setIsStreaming(true);
        setCameraReady(false);

        // Reset received caption flag when starting own stream
        hasReceivedCaptionRef.current = false;
        processedCaptionIdsRef.current.clear();
        hasShownTranslateInstructionRef.current = false;
        queuedCaptionMessagesRef.current = [];

        // Clear old captions. Camera/OCR readiness is shown in the camera UI,
        // not as a chat bubble that remains mixed into the transcript.
        clearMessages();

        // Wait for the container to be mounted
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!cameraContainerRef.current) {
            console.error("Camera container not mounted");
            setIsStreaming(false);
            triggerNotification(
                "The camera view could not open. Please try again.",
                "error"
            );
            return;
        }

        const controller = new CaptionController(
            addCaptionForCurrentMode,
            () => {
                captionControllerRef.current = null;
                queuedCaptionMessagesRef.current = [];
                setIsStreaming(false);
                setCameraReady(false);
                setShowVideo(false);
                setHasMultipleCameras(false);
                setIsSwitchingCamera(false);

                // Automatic camera closures (including the one-minute
                // no-caption timeout) should return to the normal start view.
                whatToDo({
                    setMessages,
                    onStartStream: handleStartStream,
                });
            },
            () => setCameraReady(true),
            (payload) => {
                // Limit exceeded callback
                handleLimitExceeded(payload);
            },
            setHasMultipleCameras,
            (message) => triggerNotification(message, "warn"),
            setZoomLevel
        );

        const success = await controller.startCamera(
            cameraContainerRef.current
        );
        if (success) {
            captionControllerRef.current = controller;
        } else {
            setIsStreaming(false);
        }
    };

    const handleStopStream = () => {
        closeStreamUi();

        // Show the whatToDo options again
        whatToDo({
            setMessages,
            onStartStream: handleStartStream,
        });
    };

    const handleZoom = (delta) => {
        if (captionControllerRef.current) {
            captionControllerRef.current.adjustZoom(delta);
            setZoomLevel(captionControllerRef.current.zoomLevel);
        }
    };

    const handleSwitchCamera = async () => {
        if (!captionControllerRef.current || isSwitchingCamera) return;

        setIsSwitchingCamera(true);
        try {
            const switched = await captionControllerRef.current.switchCamera();
            setZoomLevel(captionControllerRef.current?.zoomLevel || 1);

            if (!switched && captionControllerRef.current) {
                triggerNotification("Could not switch camera.", "error");
            }
        } catch (error) {
            console.error("Could not switch camera:", error);
            triggerNotification("Could not switch camera.", "error");
        } finally {
            setIsSwitchingCamera(false);
        }
    };

    const handleLogoClick = () => {
        // Stop streaming if active
        if (isStreaming) {
            handleStopStream();
        } else {
            // Reset to whatToDo
            clearMessages();
            whatToDo({
                setMessages,
                onStartStream: handleStartStream,
            });
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            // Track sign-in button click
            trackEvent("click:google_signin");

            if (isLanDevAutoLogin()) {
                const session = await apiDevLogin();
                localStorage.setItem("captions_token", session.token);
                localStorage.setItem("captions_user_id", session.userId);
                window.location.reload();
                return;
            }

            const authUrl = await initiateGoogleAuth();
            window.location.href = authUrl;
        } catch (err) {
            console.error("Error initiating Google sign-in:", err);
            triggerNotification(
                isLanDevAutoLogin()
                    ? "Local automatic sign-in failed."
                    : "Sign-in could not be started. Please try again.",
                "error"
            );
        }
    };

    // Checkout-return overlay (Fix 1) — rendered in both the logged-out and
    // logged-in branches so it shows immediately, even while the token is
    // still being validated after returning from Polar.
    const checkoutSuccessBody =
        checkoutReturn === "paid"
            ? `You're all set — ${
                  billingEntitlement?.monthly_paid_hours_total ||
                  PAID_MONTHLY_HOURS
              } hours/month.`
            : `${TOPUP_PACK_HOURS} extra hours added to your balance.`;

    const checkoutOverlayElement = (
        <AnimatePresence>
            {checkoutReturn && (
                <LimitExceededOverlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {checkoutStatus === "success" ? (
                        <>
                            <CheckoutCheck>✓</CheckoutCheck>
                            <h2>You're all set</h2>
                            <p>{checkoutSuccessBody}</p>
                            <LimitActions>
                                <LimitActionButton
                                    onClick={dismissCheckoutReturn}
                                >
                                    Continue
                                </LimitActionButton>
                            </LimitActions>
                        </>
                    ) : checkoutStatus === "processing" ? (
                        <>
                            <h2>Payment is processing</h2>
                            <p>
                                Your payment went through. It can take a minute
                                to activate — try refreshing if it doesn&apos;t
                                appear shortly.
                            </p>
                            <LimitActions>
                                <LimitActionButton
                                    onClick={() => window.location.reload()}
                                >
                                    Refresh
                                </LimitActionButton>
                                <LimitActionButtonSecondary
                                    onClick={dismissCheckoutReturn}
                                >
                                    Dismiss
                                </LimitActionButtonSecondary>
                            </LimitActions>
                        </>
                    ) : (
                        <>
                            <CheckoutSpinner />
                            <h2>Payment received</h2>
                            <p>Activating your plan…</p>
                        </>
                    )}
                </LimitExceededOverlay>
            )}
        </AnimatePresence>
    );

    // Render landing page for logged-out users
    if (!isLoggedIn && !isFreeLimitDemo) {
        return (
            <>
                <LandingPage onGoogleSignIn={handleGoogleSignIn} />
                {checkoutOverlayElement}
            </>
        );
    }

    const usageMeter = getUsageMeter(billingEntitlement);
    const showUsageMeter =
        usageMeter &&
        !isStreaming &&
        !limitExceeded &&
        !dailyCapMessage &&
        !checkoutReturn;

    const effectiveEntitlement =
        billingEntitlement ||
        limitErrorData?.entitlement || {
            plan_code: limitErrorData?.planCode || "free",
            next_reset_at: limitErrorData?.nextResetAt || null,
            monthly_free_minutes_total: FREE_MONTHLY_MINUTES,
            monthly_free_minutes_used: null,
            monthly_paid_hours_used: null,
            topup_hours_balance: null,
        };
    const limitMessaging = getLimitMessaging(effectiveEntitlement);
    const formattedReset = formatResetDate(effectiveEntitlement.next_reset_at);
    const isPaidSubscriber =
        effectiveEntitlement?.plan_code === "captions_paid";

    return (
        <Container>
            <NavSettings
                onLogoClick={handleLogoClick}
                triggerNotification={triggerNotification}
                isNavOpen={isNavOpen}
                setIsNavOpen={setIsNavOpen}
                isFeedbackModalOpen={isFeedbackModalOpen}
                setIsFeedbackModalOpen={setIsFeedbackModalOpen}
                showManageSubscription={isPaidSubscriber}
                onManageSubscription={handleManageSubscription}
                isManagingSubscription={isPortalActionLoading}
            />

            <MainContent>
                <>
                    <FixedToggleWrapper
                        $shouldBlur={
                            showWordGlossary ||
                            showAddWordModal ||
                            showBjornPromoModal ||
                            isFeedbackModalOpen ||
                            (isNavOpen && checkIfMobile())
                        }
                    >
                        <TranslateToggle />
                    </FixedToggleWrapper>

                    <ChatWrapper ref={chatWrapperRef}>
                        <ChatBox
                            messages={messages}
                            hasVideo={isStreaming}
                            shouldBlur={
                                showWordGlossary ||
                                showAddWordModal ||
                                showBjornPromoModal ||
                                isFeedbackModalOpen ||
                                (isNavOpen && checkIfMobile())
                            }
                        />
                    </ChatWrapper>

                    {/* Tooltip for word translations */}
                    <Tooltip
                        ref={tooltipRef}
                        content={tooltip.tooltipContent}
                        position={tooltip.tooltipPosition}
                        show={tooltip.showTooltip}
                        showLoader={tooltip.showWordLookupLoader}
                        forceHide={tooltip.forceHideTooltip}
                        onClick={() => {
                            if (tooltip.tooltipData) {
                                handleOpenWordGlossary(tooltip.tooltipData);
                            }
                        }}
                    />

                    <AnimatePresence>
                        {isStreaming && (
                            <VideoModalWrapper
                                initial={{
                                    x: "-50%",
                                    y: "2em",
                                    opacity: 0,
                                }}
                                animate={{
                                    x: "-50%",
                                    y: 0,
                                    opacity: 1,
                                }}
                                exit={{
                                    x: "-50%",
                                    y: "2em",
                                    opacity: 0,
                                }}
                                transition={{
                                    type: "spring",
                                    damping: 20,
                                    stiffness: 300,
                                }}
                            >
                                <VideoContainer
                                    ref={cameraContainerRef}
                                    $ready={showVideo}
                                >
                                    <VideoControls>
                                        <ZoomButton
                                            onClick={() => handleZoom(-0.5)}
                                        >
                                            −
                                        </ZoomButton>
                                        <ZoomLabel>{zoomLevel}x</ZoomLabel>
                                        <ZoomButton
                                            onClick={() => handleZoom(0.5)}
                                        >
                                            +
                                        </ZoomButton>
                                        {hasMultipleCameras && (
                                            <SwitchCameraButton
                                                type="button"
                                                onClick={handleSwitchCamera}
                                                disabled={isSwitchingCamera}
                                                aria-label="Switch camera"
                                                title="Switch camera"
                                            >
                                                ⇄
                                            </SwitchCameraButton>
                                        )}
                                    </VideoControls>
                                </VideoContainer>
                                <AnimatePresence>
                                    {!cameraReady && (
                                        <SpinnerOverlay
                                            initial={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {["downloading", "starting"].includes(
                                                ocrPreparation?.phase
                                            ) ? (
                                                <OcrProgress>
                                                    <OcrProgressLabel>
                                                        {ocrPreparation.phase ===
                                                        "downloading"
                                                            ? `Downloading caption reader… ${Math.round(ocrProgressPercent)}%`
                                                            : "Starting caption reader…"}
                                                    </OcrProgressLabel>
                                                    <OcrProgressTrack>
                                                        <OcrProgressFill
                                                            style={{
                                                                width: `${ocrProgressPercent}%`,
                                                            }}
                                                        />
                                                    </OcrProgressTrack>
                                                </OcrProgress>
                                            ) : (
                                                <Spinner />
                                            )}
                                        </SpinnerOverlay>
                                    )}
                                </AnimatePresence>
                                <CloseButton onClick={handleStopStream}>
                                    <CloseIcon />
                                </CloseButton>
                            </VideoModalWrapper>
                        )}
                    </AnimatePresence>
                </>
            </MainContent>

            {/* Word Glossary Modal */}
            <ModalWordGlossary
                glossaryData={glossaryData}
                isOpen={showWordGlossary}
                onClose={handleCloseWordGlossary}
                isLoading={isLoadingGlossary}
            />

            {/* Add Word to Folder Modal (for bjorn users) */}
            {bjornUser && (
                <ModalAddWordToFolder
                    isOpen={showAddWordModal}
                    onClose={handleCloseAddWordModal}
                    tooltipData={addWordTooltipData}
                    setTooltipData={setAddWordTooltipData}
                    bjornUserId={bjornUser.bjorn_user_id}
                    triggerNotification={triggerNotification}
                />
            )}

            {/* Bjorn Promo Modal (for non-bjorn users) */}
            <AnimatePresence mode="wait">
                {showBjornPromoModal && (
                    <PromoModalBackdrop
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                            opacity: 0,
                            transition: { duration: 0.2, ease: "easeInOut" },
                        }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        onClick={handleCloseBjornPromoModal}
                    >
                        <PromoModalContent
                            initial={{
                                opacity: 0,
                                y: "-45%",
                                x: "-50%",
                            }}
                            animate={{ opacity: 1, y: "-50%", x: "-50%" }}
                            exit={{
                                opacity: 0,
                                y: "-45%",
                                x: "-50%",
                                transition: {
                                    y: {
                                        type: "tween",
                                        duration: 0.3,
                                        ease: "easeIn",
                                    },
                                    opacity: {
                                        duration: 0.2,
                                    },
                                },
                            }}
                            transition={{
                                y: {
                                    type: "spring",
                                    bounce: 1,
                                    stiffness: 300,
                                    damping: 16,
                                    duration: 0.3,
                                },
                                opacity: {
                                    duration: 0.2,
                                    ease: "easeInOut",
                                },
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2>Save words as flashcards</h2>
                            <p>
                                Get a Björn account to save words as flashcards
                                and practice them in real-world conversations.
                            </p>
                            <a
                                href="https://heybjorn.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span>Get Björn</span>
                            </a>
                        </PromoModalContent>
                    </PromoModalBackdrop>
                )}
            </AnimatePresence>

            {/* Notifications */}
            <AnimatePresence>
                {currentNotification && (
                    <Notification
                        type={currentNotification.type}
                        persistent={currentNotification.persistent}
                        onClose={currentNotification.onClose}
                    >
                        {currentNotification.message}
                    </Notification>
                )}
            </AnimatePresence>

            {/* Usage Limit Exceeded Overlay */}
            <AnimatePresence>
                {limitExceeded && (
                    <LimitExceededOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <h2>{limitMessaging.title}</h2>
                        <p>{limitMessaging.body}</p>
                        {limitMessaging.details && (
                            <LimitMeta>{limitMessaging.details}</LimitMeta>
                        )}
                        {formattedReset && (
                            <LimitMeta>
                                Usage resets on {formattedReset}.
                            </LimitMeta>
                        )}
                        <LimitActions>
                            <LimitActionButton
                                disabled={isBillingActionLoading}
                                onClick={() =>
                                    handleBillingAction(limitMessaging.actionType)
                                }
                            >
                                {isBillingActionLoading
                                    ? "Opening checkout..."
                                    : limitMessaging.ctaLabel}
                            </LimitActionButton>
                        </LimitActions>
                    </LimitExceededOverlay>
                )}
            </AnimatePresence>

            {/* Daily IP-cap limit (Fix 3) — no subscribe button */}
            <AnimatePresence>
                {dailyCapMessage && (
                    <LimitExceededOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <h2>Daily limit reached</h2>
                        <p>{dailyCapMessage}</p>
                        <LimitActions>
                            <LimitActionButton
                                onClick={() => setDailyCapMessage(null)}
                            >
                                Got it
                            </LimitActionButton>
                        </LimitActions>
                    </LimitExceededOverlay>
                )}
            </AnimatePresence>

            {/* Always-visible usage meter (Fix 2) */}
            {showUsageMeter && (
                <UsageMeter>
                    <UsageMeterLabel>{usageMeter.label}</UsageMeterLabel>
                    <UsageMeterTrack>
                        <UsageMeterFill
                            $fraction={usageMeter.fraction}
                            $low={usageMeter.low}
                        />
                    </UsageMeterTrack>
                </UsageMeter>
            )}

            {/* Checkout return overlay (Fix 1) */}
            {checkoutOverlayElement}
        </Container>
    );
};

export default Main;
